import { useEffect, useState } from "react";
import { categoriesApi } from "../api/client.js";

// Admin-controlled category visibility (Admin > Categories). Shared by every
// page that filters the catalog by category, so a category hidden by an
// admin disappears everywhere consistently instead of page-by-page.
const CACHE_KEY = "rf_disabled_categories_v1";

function readCachedDisabledCategories() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? new Set(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Returns a Set of disabled category ids, or null before the very first
 * fetch ever completes on this browser. Starts from the cached value (from
 * a previous visit) so repeat visits apply instantly with no flash; a
 * failed fetch just leaves whatever was already showing.
 */
export function useDisabledCategories() {
  const [disabledCategories, setDisabledCategories] = useState(readCachedDisabledCategories);

  useEffect(() => {
    let cancelled = false;
    categoriesApi
      .list()
      .then(({ categories }) => {
        if (cancelled) return;
        const ids = (categories || []).filter((c) => !c.enabled).map((c) => c.id);
        setDisabledCategories(new Set(ids));
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(ids));
        } catch {
          /* ignore — cache is a nice-to-have, not required */
        }
      })
      .catch(() => {
        /* API unreachable — leave whatever was already showing */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return disabledCategories;
}
